import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CartDrawerService } from 'src/app/provide/cart-drawer.service';
import { CommonService } from 'src/app/provide/common.service';
import { AuthService } from 'src/app/provide/auth.service';
import { DeliveryOptionsService } from 'src/app/provide/delivery-options.service';

@Component({
  selector: 'app-cart-drawer',
  templateUrl: './cart-drawer.component.html',
  styleUrls: ['./cart-drawer.component.scss'],
  standalone: false
})
export class CartDrawerComponent implements OnInit, OnDestroy {
  isOpen: boolean = false;
  cartListDetails: any[] = [];
  payOutDetail: any = null;
  selectedDeliveryOption: any = null;
  optionList: any[] = [];
  loadingButton: any = [];
  erorMessageArr: any = [];
  isCartLoading: boolean = false;
  
  private subscriptions: Subscription[] = [];

  constructor(
    private cartDrawerService: CartDrawerService,
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private deliveryOptionsService: DeliveryOptionsService
  ) {}

  ngOnInit(): void {
    // Subscribe to drawer open/close state
    const drawerSub = this.cartDrawerService.isOpen$.subscribe(isOpen => {
      this.isOpen = isOpen;
      if (isOpen) {
        this.loadCartData();
        this.getDeliveryOptions();
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(drawerSub);

    // Subscribe to delivery option changes from service
    const deliveryOptionSub = this.deliveryOptionsService.deliveryOption$.subscribe(option => {
      if (option) {
        this.selectedDeliveryOption = option;
        this.updatePayOutDetailFromOption(option);
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(deliveryOptionSub);

    // Subscribe to option list changes
    const optionListSub = this.deliveryOptionsService.optionList$.subscribe(options => {
      if (options && options.length > 0) {
        this.optionList = options;
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(optionListSub);

    // Load initial cart data if drawer is open
    if (this.cartDrawerService.getIsOpen()) {
      this.loadCartData();
      this.getDeliveryOptions();
    }

    // Load option list from service if available
    const existingOptionList = this.deliveryOptionsService.getOptionList();
    if (existingOptionList && existingOptionList.length > 0) {
      this.optionList = existingOptionList;
    }

    // Load selected delivery option from service if available
    const existingOption = this.deliveryOptionsService.getDeliveryOption();
    if (existingOption) {
      this.selectedDeliveryOption = existingOption;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadCartData(): void {
    const isLoggedIn = this.authService.isLogin();
    if (!isLoggedIn) {
      this.cartListDetails = [];
      this.payOutDetail = null;
      this.isCartLoading = false;
      return;
    }

    this.isCartLoading = true;
    this.commonService.getApi('user/cartList', 1).subscribe(
      (res: any) => {
        this.isCartLoading = false;
        if (res.responseCode === 200 && res.result) {
          this.cartListDetails = res.result.cartList || [];
          this.payOutDetail = res.result.payOutAmount || {
            subTotal: 0,
            deliveryFee: 0,
            totalAmount: 0,
            deliveryMode: 'standard'
          };
          
          // Sync delivery option from payOutDetail or service
          if (this.payOutDetail.deliveryMode && this.optionList && this.optionList.length > 0) {
            const matchedOption = this.optionList.find(
              (option) => option.deliveryType === this.payOutDetail.deliveryMode
            );
            if (matchedOption) {
              this.selectedDeliveryOption = matchedOption;
              this.deliveryOptionsService.setDeliveryOption(matchedOption);
            }
          } else if (this.payOutDetail.deliveryMode) {
            // Create a minimal option object if optionList not loaded yet
            const option = {
              deliveryType: this.payOutDetail.deliveryMode,
              deliveryAmount: this.payOutDetail.deliveryFee || 0,
              weight: this.payOutDetail.weight,
              volume: this.payOutDetail.volume
            };
            this.selectedDeliveryOption = option;
          }
          
          this.cdr.detectChanges();
        } else {
          this.cartListDetails = [];
          this.payOutDetail = null;
        }
      },
      (err) => {
        this.isCartLoading = false;
        this.cartListDetails = [];
        this.payOutDetail = null;
      }
    );
  }

  closeDrawer(): void {
    this.cartDrawerService.close();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeDrawer();
    }
  }

  updateCartValue(id: string, priceSize: any, newQuantity: number, currentQty: number, index: number, availableQty: number): void {
    // Respect available stock if provided
    if (availableQty != null && newQuantity > availableQty) {
      this.erorMessageArr[index] = `Only ${availableQty} in stock.`;
      setTimeout(() => {
        this.erorMessageArr[index] = '';
      }, 2000);
      return;
    }

    // Find the cart item index
    const itemIndex = this.cartListDetails.findIndex(item => item._id === id);
    if (itemIndex === -1) {
      return;
    }

    // Keep original copies for rollback in case API fails
    const originalItem = { ...this.cartListDetails[itemIndex] };
    const originalPayout = this.payOutDetail ? { ...this.payOutDetail } : null;

    // Optimistic UI update: update quantity locally so user sees change immediately
    this.cartListDetails[itemIndex].quantity = newQuantity;
    this.recalculateTotals();
    this.cdr.detectChanges();

    this.loadingButton[index] = true; // kept for potential future visual states (not used to disable buttons)
    const apiReqData = {
      quantity: newQuantity,
      priceSizeDetails: {
        value: priceSize.value,
        price: Number(priceSize.price),
        unit: priceSize.unit,
        weight: priceSize.weight,
        volume: priceSize.volume,
        id: priceSize.id
      }
    };

    // Use PUT, same as main cart page logic
    this.commonService.putApi(`user/updateCartItem?_id=${id}`, apiReqData, 1).subscribe(
      (res: any) => {
        this.loadingButton[index] = false;
        if (res.responseCode === 200) {
          // Update header cart count
          this.commonService.myShopingBagApi();

          // Optionally sync payout detail from server if provided (without reloading list)
          if (res.result && res.result.payOutAmount) {
            const currentDeliveryFee = this.payOutDetail?.deliveryFee;
            const currentDeliveryMode = this.payOutDetail?.deliveryMode;

            this.payOutDetail = res.result.payOutAmount;

            if (currentDeliveryFee !== undefined && currentDeliveryFee !== null) {
              this.payOutDetail.deliveryFee = currentDeliveryFee;
              this.payOutDetail.deliveryMode = currentDeliveryMode;
              this.payOutDetail.totalAmount = (this.payOutDetail.subTotal || 0) + currentDeliveryFee;
            }
          }
        } else {
          // Rollback on error
          this.cartListDetails[itemIndex] = originalItem;
          if (originalPayout) {
            this.payOutDetail = originalPayout;
          }
          this.recalculateTotals();
          this.commonService.errorToast(res.responseMessage || 'Failed to update cart');
        }
      },
      (err) => {
        this.loadingButton[index] = false;
        // Rollback on error
        this.cartListDetails[itemIndex] = originalItem;
        if (originalPayout) {
          this.payOutDetail = originalPayout;
        }
        this.recalculateTotals();
        this.commonService.errorToast('Failed to update cart. Please try again.');
      }
    );
  }

  private recalculateTotals(): void {
    if (!this.payOutDetail) {
      return;
    }

    let subTotal = 0;
    for (const item of this.cartListDetails) {
      const price = this.getItemPrice(item);
      subTotal += price * (item?.quantity || 0);
    }

    this.payOutDetail.subTotal = subTotal;
    const deliveryFee = this.payOutDetail.deliveryFee || 0;
    this.payOutDetail.totalAmount = subTotal + deliveryFee;
  }

  removeCartItem(itemId: string, index: number): void {
    // Find index if not provided or out of sync
    if (index == null || index < 0 || index >= this.cartListDetails.length || this.cartListDetails[index]?._id !== itemId) {
      index = this.cartListDetails.findIndex(item => item._id === itemId);
    }
    if (index === -1) {
      return;
    }

    const removedItem = this.cartListDetails[index];

    // Optimistic UI: remove the item locally so only that card disappears
    this.cartListDetails.splice(index, 1);
    this.recalculateTotals();
    
    // Update header cart count IMMEDIATELY (optimistic update)
    // Calculate unique product count from current cartListDetails
    const uniqueProducts = new Set();
    this.cartListDetails.forEach(item => {
      if (item.productId && item.productId._id) {
        uniqueProducts.add(item.productId._id);
      }
    });
    // Update the count immediately
    this.commonService.totalItemsInShoppingBag.next(uniqueProducts.size);
    
    this.cdr.detectChanges();

    // Call API in background without reloading entire cart drawer
    this.commonService.deleteApi(`user/removeItemFromCart?_id=${itemId}`, 1).subscribe(
      (res: any) => {
        if (res.responseCode === 200) {
          // Refresh cart count from server to ensure accuracy
          this.commonService.myShopingBagApi();
          
          // Trigger cart refresh for other components (like mycart-layout)
          this.commonService.triggerCartRefresh();
          
          // Reload cart data to sync with mycart-layout if it's open
          this.loadCartData();
        } else {
          // Rollback if delete failed on server
          this.cartListDetails.splice(index, 0, removedItem);
          this.recalculateTotals();
          
          // Recalculate and update count
          const uniqueProductsRollback = new Set();
          this.cartListDetails.forEach(item => {
            if (item.productId && item.productId._id) {
              uniqueProductsRollback.add(item.productId._id);
            }
          });
          this.commonService.totalItemsInShoppingBag.next(uniqueProductsRollback.size);
          
          this.cdr.detectChanges();
          this.commonService.errorToast(res.responseMessage || 'Failed to remove item');
        }
      },
      (err) => {
        // On error, rollback locally
        this.cartListDetails.splice(index, 0, removedItem);
        this.recalculateTotals();
        
        // Recalculate and update count
        const uniqueProductsRollback = new Set();
        this.cartListDetails.forEach(item => {
          if (item.productId && item.productId._id) {
            uniqueProductsRollback.add(item.productId._id);
          }
        });
        this.commonService.totalItemsInShoppingBag.next(uniqueProductsRollback.size);
        
        this.cdr.detectChanges();
        this.commonService.errorToast('Failed to remove item. Please try again.');
      }
    );
  }

  clearAllItems(): void {
    if (!this.cartListDetails || this.cartListDetails.length === 0) {
      return;
    }

    if (confirm('Are you sure you want to clear all items from your cart?')) {
      this.commonService.showSpinner();
      // Remove all items one by one or use a bulk delete API if available
      let deletePromises = this.cartListDetails.map(item => 
        this.commonService.deleteApi(`user/removeItemFromCart?_id=${item._id}`, 1).toPromise()
      );

      Promise.all(deletePromises).then(() => {
        this.commonService.hideSpinner();
        this.commonService.myShopingBagApi();
        this.loadCartData();
        // Toast notification removed
      }).catch(() => {
        this.commonService.hideSpinner();
        this.commonService.errorToast('Failed to clear cart. Please try again.');
      });
    }
  }

  proceedToCheckout(): void {
    // Get payOutDetail from localStorage
    const storedDetail = localStorage.getItem('payOutDetail');
    if (!storedDetail) {
      this.commonService.errorToast('Please select delivery option first.');
      return;
    }

    try {
      const payOutDetail = JSON.parse(storedDetail);
      const deliveryMode = payOutDetail.deliveryMode || 'STANDARD';
      const weight = payOutDetail.weight || 'XXX-Heavy';
      const volume = payOutDetail.volume || 'XXX-Large';

      // Call the proceed API (same as mycart-layout)
      this.commonService.showSpinner();
      const apiReqUrl = `deliveryFee/proceedToCheckOut?deliveryType=${deliveryMode}&weight=${weight}&volume=${volume}`;

      this.commonService.getApi(apiReqUrl, 1).subscribe(
        (res: any) => {
          this.commonService.hideSpinner();
          if (res.responseCode === 200) {
            // Close drawer and navigate to address selection page first (same as mycart-layout)
            this.closeDrawer();
            this.router.navigate(['/list-address-customer']);
          } else {
            this.commonService.errorToast(res.responseMessage || 'Failed to proceed to checkout');
          }
        },
        (err: any) => {
          this.commonService.hideSpinner();
          this.commonService.errorToast(err.responseMessage || 'Failed to proceed to checkout');
        }
      );
    } catch (error) {
      this.commonService.hideSpinner();
      this.commonService.errorToast('Invalid payment details. Please refresh the page.');
      console.error('Error parsing payOutDetail:', error);
    }
  }

  // Navigate to product detail (customer-category-particular-view)
  goToProduct(product: any): void {
    if (!product?._id) {
      return;
    }

    const categoryId = product?.categoryId?._id;
    const subCategoryId = product?.subCategoryId?._id;

    const queryParams: any = { _id: product._id };
    if (categoryId) {
      queryParams.categoryId = categoryId;
    }
    if (subCategoryId) {
      queryParams.subCategoryId = subCategoryId;
    }

    this.closeDrawer();

    // Always force navigation, even if already on the same route/URL
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => {
        this.router.navigate(['/customer-category-particular-view'], { queryParams });
      });
  }

  viewCart(): void {
    this.closeDrawer();
    this.router.navigate(['/addtoCart-customer']);
  }

  continueShopping(): void {
    this.closeDrawer();
    this.router.navigate(['/home']);
  }

  getPrice(price: any): string {
    return this.commonService.getPrice(price);
  }

  getItemPrice(item: any): number {
    if (item?.isDealActive && item?.dealPrice) {
      return item.dealPrice;
    }
    return item?.priceSizeDetails?.price || 0;
  }

  getItemTotal(item: any): number {
    const price = this.getItemPrice(item);
    return price * (item?.quantity || 0);
  }

  getCartTotal(): number {
    if (!this.payOutDetail) {
      return 0;
    }
    return this.payOutDetail.totalAmount || 0;
  }

  getCartSubtotal(): number {
    if (!this.payOutDetail) {
      return 0;
    }
    return this.payOutDetail.subTotal || 0;
  }

  isFreeDeliveryUnlocked(): boolean {
    const subtotal = this.getCartSubtotal();
    return subtotal >= 700;
  }

  // Fetch delivery options from API
  getDeliveryOptions(): void {
    const apiReqUrl = `deliveryFee/listOption`;
    this.commonService.getApi(apiReqUrl, 1).subscribe(
      (res: any) => {
        if (res.responseCode === 200 && res.result) {
          this.optionList = res.result;
          this.deliveryOptionsService.setOptionList(res.result);
          
          // If we have a delivery mode but no selected option, find and set it
          if (this.payOutDetail?.deliveryMode && !this.selectedDeliveryOption) {
            const matchedOption = this.optionList.find(
              (option) => option.deliveryType === this.payOutDetail.deliveryMode
            );
            if (matchedOption) {
              this.selectedDeliveryOption = matchedOption;
              this.deliveryOptionsService.setDeliveryOption(matchedOption);
            }
          }
          this.cdr.detectChanges();
        }
      },
      (err) => {
        console.error('Error fetching delivery options:', err);
      }
    );
  }

  // Handle delivery option selection from dropdown
  onDeliverySelectChange(deliveryType: string): void {
    if (!deliveryType || !this.optionList || this.optionList.length === 0) {
      return;
    }

    const selectedOption = this.optionList.find(option => option.deliveryType === deliveryType);
    if (selectedOption) {
      this.onSelectDeliveryOption(selectedOption);
    }
  }

  // Handle delivery option selection from cart-drawer
  onSelectDeliveryOption(option: any): void {
    if (!option) {
      return;
    }

    this.selectedDeliveryOption = option;
    
    // Update via service to sync with mycart-layout
    this.deliveryOptionsService.setDeliveryOption(option);
    
    // Update local payOutDetail
    this.updatePayOutDetailFromOption(option);
  }

  // Update payOutDetail from selected option
  private updatePayOutDetailFromOption(option: any): void {
    if (!this.payOutDetail || !option) {
      return;
    }

    this.payOutDetail.deliveryFee = option.deliveryAmount || 0;
    this.payOutDetail.deliveryMode = option.deliveryType;
    this.payOutDetail.weight = option.weight;
    this.payOutDetail.volume = option.volume;

    // Recalculate total
    const subTotal = this.payOutDetail.subTotal || 0;
    this.payOutDetail.totalAmount = subTotal + (option.deliveryAmount || 0);

    // Persist to localStorage
    localStorage.setItem('payOutDetail', JSON.stringify(this.payOutDetail));
    
    this.cdr.detectChanges();
  }

  // Get delivery option display name
  getDeliveryName(deliveryType: string): string {
    if (!deliveryType) return 'Standard';
    
    const deliveryMap: { [key: string]: string } = {
      'SUNDAY': 'Sunday',
      'SAMEDAY': 'Sameday',
      'BUSINESSDAY': 'Next business day',
      'SATURDAY': 'Saturday',
      'STANDARD': 'Standard'
    };
    
    return deliveryMap[deliveryType] || deliveryType;
  }

  getDeliveryFeeDisplay(): string {
    if (this.payOutDetail && this.payOutDetail.deliveryFee !== undefined) {
      if (this.payOutDetail.deliveryFee === 0) {
        return 'FREE';
      } else {
        return 'R' + this.payOutDetail.deliveryFee.toFixed(2);
      }
    }
    return 'FREE';
  }
}

